import * as React from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { PricingResult } from '@/types/pricing-output';
import {
  comparisonService,
  LoanComparisonData,
} from '@/services/comparison.service';
import { exportService } from '@/services/export.service';
import { LoanValuation } from '@/services/portfolio.service';
import { toast } from 'react-hot-toast';
import { Loader2, Download, Plus, X } from 'lucide-react';
import { formatCurrency as formatCurrencyHelper } from '@/utils/helpers';

// Mock data for comparison - same loan with different scenarios
const mockComparisonData: PricingResult[] = [
  {
    loan_id: 1,
    pricing_scenario: 'Base Case',
    maturity_assumption: 'Maturity',
    credit_spread: 1.75,
    market_yield: 6.31,
    loss_scenario: 'N',
    risk_metrics: {
      pd: 0.0,
      ead: 100.0,
      lgd: 100.0,
      lag_to_recovery: 0,
      default_date: '01.09.47',
      cdr: '0%',
    },
    loan_identification: {
      client_loan_number: '100010001',
      loan_name: 'Loan Name 100010001',
    },
    property_details: {
      sector: 'Multifamily',
      property_type: 'Afordable Housing',
      property_lifecycle_financing: 'Permanent',
    },
    borrower: {
      sponsor_borrower: 'Sponsor Name',
    },
    loan_amounts: {
      original_balance: 15210000,
      current_balance: 13915042,
      currency: 'USD',
      client_percentage: 100.0,
      pik_balance: ' -   ',
    },
    loan_structure: {
      position_in_capital_stack: 'Senior Loan - A Note',
      periodicity: 'Monthly',
      interest_day_count: '30/360',
      loan_status: 'Performing',
      commentary: 'Current',
      coupon_description: 'Fixed @5.75%',
      contract_type: 'Fixed',
      interest_type: 'Accrued Interest',
    },
    valuation_results: {
      fair_value_plus_accrued: 13371709,
      accrued_interest: 66676,
      fair_value: 13305033,
      price_including_accrued: 96.1,
      price: 95.62,
      benchmark_yield: 4.56,
      benchmark: 'US Treasury',
      wal_years: 14.79,
      modified_duration_years: 8.61,
      convexity: 116.7,
      maturity: '01.09.47',
    },
    fair_value_ranges: {
      lower_price: 93.83,
      upper_price: 97.46,
      lower_fv: 13055931,
      upper_fv: 13561455,
      lower_mey: 6.44,
      upper_mey: 6.03,
    },
    dates: {
      loan_origination_date: '01.09.47',
      original_maturity_date: '01.09.47',
      extension_1st: null,
      extension_2nd: null,
      extension_3rd: null,
    },
    default_scenario: 'N',
    component_yield: 6.23,
    price_changes: {
      delta_price: 0.0,
      delta_price_yield_cbe: 0.0,
      delta_price_credit_spread: 0.0,
      delta_price_benchmark: 0.0,
      delta_price_yield_curve_shift: 0.0,
      delta_price_yield_curve_roll: 0.0,
      delta_price_accretion: 0.0,
    },
    yield_changes: {
      delta_credit_spread: 0.0,
      delta_benchmark_yield: 0.0,
      delta_cbe_yield: 0.0,
      yield_curve_shift: 0.0,
      yield_curve_roll: 0.0,
    },
    prior_scenario: {
      scenario: 'Maturity',
      credit_spread: 1.75,
      market_yield: 6.31,
      maturity_scenario: 'Maturity',
      amortization_type: 'Amortizing',
      property_location: 'City, ST',
      dscr: null,
      ltv: null,
      current_balance_prior: 13915042,
      price_prior: 95.62,
      benchmark_yield_prior: 0,
      credit_spread_prior: 1.75,
      market_yield_prior: 6.31,
      dscr_prior: null,
      ltv_prior: null,
      wal_prior: 14.79,
      duration_prior: 8.61,
      loan_status_prior: 'Performing',
    },
    portfolio_summary: {
      balance_current: 13915042,
      ltv_current: null,
      ltv_prior: 0.0,
      dscr_current: null,
      dscr_prior: null,
      benchmark_yield: 4.56,
      sector: 'Multifamily',
      fixed_float: 'Fixed',
      prior_balance: 13915042,
      prior_benchmark_yield: 4.56,
      prior_wal: 14.79,
      prior_duration: 8.61,
      prior_loan_status: 'Performing',
    },
    sector_allocation_summary:
      'Multifamily (25.9%), Hotel (25%), Office (24.7%), Industrial (13.7%), Retail (7.5%), Mixed Use, (3.1%), ',
  },
  {
    loan_id: 1,
    pricing_scenario: 'Stress Case',
    maturity_assumption: 'Extension - 1st',
    credit_spread: 3.5,
    market_yield: 8.06,
    loss_scenario: 'N',
    risk_metrics: {
      pd: 5.0,
      ead: 100.0,
      lgd: 100.0,
      lag_to_recovery: 0,
      default_date: '01.09.47',
      cdr: '0%',
    },
    loan_identification: {
      client_loan_number: '100010001',
      loan_name: 'Loan Name 100010001',
    },
    property_details: {
      sector: 'Multifamily',
      property_type: 'Afordable Housing',
      property_lifecycle_financing: 'Permanent',
    },
    borrower: {
      sponsor_borrower: 'Sponsor Name',
    },
    loan_amounts: {
      original_balance: 15210000,
      current_balance: 13915042,
      currency: 'USD',
      client_percentage: 100.0,
      pik_balance: ' -   ',
    },
    loan_structure: {
      position_in_capital_stack: 'Senior Loan - A Note',
      periodicity: 'Monthly',
      interest_day_count: '30/360',
      loan_status: 'Performing',
      commentary: 'Current',
      coupon_description: 'Fixed @5.75%',
      contract_type: 'Fixed',
      interest_type: 'Accrued Interest',
    },
    valuation_results: {
      fair_value_plus_accrued: 12500000,
      accrued_interest: 66676,
      fair_value: 12433324,
      price_including_accrued: 89.8,
      price: 89.32,
      benchmark_yield: 4.56,
      benchmark: 'US Treasury',
      wal_years: 14.79,
      modified_duration_years: 8.61,
      convexity: 116.7,
      maturity: '01.09.47',
    },
    fair_value_ranges: {
      lower_price: 87.5,
      upper_price: 91.2,
      lower_fv: 12187500,
      upper_fv: 12690000,
      lower_mey: 8.2,
      upper_mey: 7.9,
    },
    dates: {
      loan_origination_date: '01.09.47',
      original_maturity_date: '01.09.47',
      extension_1st: null,
      extension_2nd: null,
      extension_3rd: null,
    },
    default_scenario: 'N',
    component_yield: 7.98,
    price_changes: {
      delta_price: -6.3,
      delta_price_yield_cbe: -3.15,
      delta_price_credit_spread: -3.15,
      delta_price_benchmark: 0.0,
      delta_price_yield_curve_shift: 0.0,
      delta_price_yield_curve_roll: 0.0,
      delta_price_accretion: 0.0,
    },
    yield_changes: {
      delta_credit_spread: 1.75,
      delta_benchmark_yield: 0.0,
      delta_cbe_yield: 1.75,
      yield_curve_shift: 0.0,
      yield_curve_roll: 0.0,
    },
    prior_scenario: {
      scenario: 'Maturity',
      credit_spread: 1.75,
      market_yield: 6.31,
      maturity_scenario: 'Maturity',
      amortization_type: 'Amortizing',
      property_location: 'City, ST',
      dscr: null,
      ltv: null,
      current_balance_prior: 13915042,
      price_prior: 95.62,
      benchmark_yield_prior: 0,
      credit_spread_prior: 1.75,
      market_yield_prior: 6.31,
      dscr_prior: null,
      ltv_prior: null,
      wal_prior: 14.79,
      duration_prior: 8.61,
      loan_status_prior: 'Performing',
    },
    portfolio_summary: {
      balance_current: 13915042,
      ltv_current: null,
      ltv_prior: 0.0,
      dscr_current: null,
      dscr_prior: null,
      benchmark_yield: 4.56,
      sector: 'Multifamily',
      fixed_float: 'Fixed',
      prior_balance: 13915042,
      prior_benchmark_yield: 4.56,
      prior_wal: 14.79,
      prior_duration: 8.61,
      prior_loan_status: 'Performing',
    },
    sector_allocation_summary:
      'Multifamily (25.9%), Hotel (25%), Office (24.7%), Industrial (13.7%), Retail (7.5%), Mixed Use, (3.1%), ',
  },
  {
    loan_id: 1,
    pricing_scenario: 'Default Case',
    maturity_assumption: 'Extension - 1st',
    credit_spread: 13.5,
    market_yield: 19.01,
    loss_scenario: 'Y - PD',
    risk_metrics: {
      pd: 100.0,
      ead: 100.0,
      lgd: 23.0,
      lag_to_recovery: 0,
      default_date: '01.10.24',
      cdr: '0%',
    },
    loan_identification: {
      client_loan_number: '100010001',
      loan_name: 'Loan Name 100010001',
    },
    property_details: {
      sector: 'Multifamily',
      property_type: 'Afordable Housing',
      property_lifecycle_financing: 'Permanent',
    },
    borrower: {
      sponsor_borrower: 'Sponsor Name',
    },
    loan_amounts: {
      original_balance: 15210000,
      current_balance: 13915042,
      currency: 'USD',
      client_percentage: 100.0,
      pik_balance: ' -   ',
    },
    loan_structure: {
      position_in_capital_stack: 'Senior Loan - A Note',
      periodicity: 'Monthly',
      interest_day_count: '30/360',
      loan_status: 'Default',
      commentary: 'Default scenario',
      coupon_description: 'Fixed @5.75%',
      contract_type: 'Fixed',
      interest_type: 'Accrued Interest',
    },
    valuation_results: {
      fair_value_plus_accrued: 10700000,
      accrued_interest: 66676,
      fair_value: 10633324,
      price_including_accrued: 76.9,
      price: 76.42,
      benchmark_yield: 4.56,
      benchmark: 'US Treasury',
      wal_years: 0.58,
      modified_duration_years: 0.45,
      convexity: 0.25,
      maturity: '01.10.24',
    },
    fair_value_ranges: {
      lower_price: 75.5,
      upper_price: 77.3,
      lower_fv: 10500000,
      upper_fv: 10750000,
      lower_mey: 19.5,
      upper_mey: 18.5,
    },
    dates: {
      loan_origination_date: '01.09.47',
      original_maturity_date: '01.09.47',
      extension_1st: '01.09.26',
      extension_2nd: null,
      extension_3rd: null,
    },
    default_scenario: 'Y - PD',
    component_yield: 18.3,
    price_changes: {
      delta_price: -19.2,
      delta_price_yield_cbe: -12.0,
      delta_price_credit_spread: -7.2,
      delta_price_benchmark: 0.0,
      delta_price_yield_curve_shift: 0.0,
      delta_price_yield_curve_roll: 0.0,
      delta_price_accretion: 0.0,
    },
    yield_changes: {
      delta_credit_spread: 11.75,
      delta_benchmark_yield: 0.0,
      delta_cbe_yield: 12.07,
      yield_curve_shift: 0.0,
      yield_curve_roll: 0.0,
    },
    prior_scenario: {
      scenario: 'Maturity',
      credit_spread: 1.75,
      market_yield: 6.31,
      maturity_scenario: 'Maturity',
      amortization_type: 'Amortizing',
      property_location: 'City, ST',
      dscr: null,
      ltv: null,
      current_balance_prior: 13915042,
      price_prior: 95.62,
      benchmark_yield_prior: 0,
      credit_spread_prior: 1.75,
      market_yield_prior: 6.31,
      dscr_prior: null,
      ltv_prior: null,
      wal_prior: 14.79,
      duration_prior: 8.61,
      loan_status_prior: 'Performing',
    },
    portfolio_summary: {
      balance_current: 13915042,
      ltv_current: null,
      ltv_prior: 0.0,
      dscr_current: null,
      dscr_prior: null,
      benchmark_yield: 4.56,
      sector: 'Multifamily',
      fixed_float: 'Fixed',
      prior_balance: 13915042,
      prior_benchmark_yield: 4.56,
      prior_wal: 14.79,
      prior_duration: 8.61,
      prior_loan_status: 'Performing',
    },
    sector_allocation_summary:
      'Multifamily (25.9%), Hotel (25%), Office (24.7%), Industrial (13.7%), Retail (7.5%), Mixed Use, (3.1%), ',
  },
];

const PricingComparisonPage: React.FC = () => {
  const [comparisonData] = React.useState<PricingResult[]>(mockComparisonData);
  const [loanComparisons, setLoanComparisons] = React.useState<
    LoanComparisonData[]
  >([]);
  const [selectedLoans, setSelectedLoans] = React.useState<number[]>([]);
  const [availableLoans, setAvailableLoans] = React.useState<
    Array<{ id: number; name: string; balance: number; sector: string }>
  >([]);
  const [loading, setLoading] = React.useState(false);
  const [exporting, setExporting] = React.useState(false);
  const [showLoanSelector, setShowLoanSelector] = React.useState(false);
  const [selectedMetrics] = React.useState<string[]>([
    'price',
    'yield',
    'fair_value',
    'duration',
    'credit_spread',
  ]);

  // Load available loans on mount
  React.useEffect(() => {
    loadAvailableLoans();
  }, []);

  // Load comparison data when selected loans change
  React.useEffect(() => {
    if (selectedLoans.length > 0) {
      loadComparisonData();
    }
  }, [selectedLoans]);

  const loadAvailableLoans = async () => {
    try {
      const loans = await comparisonService.getAvailableLoans();
      setAvailableLoans(loans);
      // Pre-select first two loans if available
      if (loans.length >= 2) {
        setSelectedLoans([loans[0].id, loans[1].id]);
      } else if (loans.length === 1) {
        setSelectedLoans([loans[0].id]);
      }
    } catch (error) {
      console.error('Error loading available loans:', error);
      toast.error('Failed to load available loans');
    }
  };

  const loadComparisonData = async () => {
    if (selectedLoans.length === 0) return;

    try {
      setLoading(true);
      const data = await comparisonService.getLoansForComparison(selectedLoans);
      setLoanComparisons(data);
    } catch (error) {
      console.error('Error loading comparison data:', error);
      toast.error('Failed to load comparison data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddLoan = (loanId: number) => {
    if (!selectedLoans.includes(loanId) && selectedLoans.length < 4) {
      setSelectedLoans([...selectedLoans, loanId]);
    }
    setShowLoanSelector(false);
  };

  const handleRemoveLoan = (loanId: number) => {
    setSelectedLoans(selectedLoans.filter((id) => id !== loanId));
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const blob = await exportService.exportPricingResultsToExcel();
      const filename = `pricing_comparison_${new Date().toISOString().split('T')[0]}.xlsx`;
      exportService.downloadFile(blob, filename);
      toast.success('Comparison exported to Excel successfully');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export comparison');
    } finally {
      setExporting(false);
    }
  };

  // Use global formatCurrency from helpers (defaults to USD for aggregated data)
  const formatCurrency = (value: number, currency: 'USD' | 'EUR' | 'GBP' | 'CAD' | 'AUD' = 'USD') => {
    return formatCurrencyHelper(value, 0, currency);
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(2)}%`;
  };

  const getScenarioColor = (scenario: string) => {
    switch (scenario.toLowerCase()) {
      case 'base case':
        return 'bg-blue-50 border-blue-200';
      case 'stress case':
        return 'bg-yellow-50 border-yellow-200';
      case 'default case':
        return 'bg-red-50 border-red-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  const getMetricValueFromValuation = (
    valuation: LoanValuation | undefined,
    metric: string
  ) => {
    if (!valuation) return '—';

    switch (metric) {
      case 'price':
        return formatPercentage(valuation.price_clean_decimal);
      case 'yield':
        return formatPercentage(valuation.component_yield_pct);
      case 'fair_value':
        return formatCurrency(valuation.fair_value_clean);
      case 'duration':
        return `${valuation.modified_duration_years.toFixed(1)}y`;
      case 'credit_spread':
        return formatPercentage(valuation.credit_spread);
      case 'market_yield':
        return formatPercentage(valuation.component_yield_pct);
      case 'wal':
        return `${valuation.wal_years.toFixed(1)}y`;
      case 'convexity':
        return valuation.convexity.toFixed(1);
      case 'pd':
        return formatPercentage(valuation.pd || 0);
      case 'lgd':
        return formatPercentage(valuation.lgd || 0);
      default:
        return 'N/A';
    }
  };

  const getMetricValue = (loan: PricingResult, metric: string) => {
    const currency = (loan.loan_amounts?.currency || 'USD') as 'USD' | 'EUR' | 'GBP' | 'CAD' | 'AUD';
    switch (metric) {
      case 'price':
        return formatPercentage(loan.valuation_results.price);
      case 'yield':
        return formatPercentage(loan.component_yield);
      case 'fair_value':
        return formatCurrency(loan.valuation_results.fair_value, currency);
      case 'duration':
        return `${loan.valuation_results.modified_duration_years.toFixed(1)}y`;
      case 'credit_spread':
        return formatPercentage(loan.credit_spread);
      case 'market_yield':
        return formatPercentage(loan.market_yield);
      case 'wal':
        return `${loan.valuation_results.wal_years.toFixed(1)}y`;
      case 'convexity':
        return loan.valuation_results.convexity.toFixed(1);
      case 'pd':
        return formatPercentage(loan.risk_metrics.pd);
      case 'lgd':
        return formatPercentage(loan.risk_metrics.lgd);
      default:
        return 'N/A';
    }
  };

  const getMetricLabel = (metric: string) => {
    switch (metric) {
      case 'price':
        return 'Price';
      case 'yield':
        return 'Yield';
      case 'fair_value':
        return 'Fair Value';
      case 'duration':
        return 'Duration';
      case 'credit_spread':
        return 'Credit Spread';
      case 'market_yield':
        return 'Market Yield';
      case 'wal':
        return 'WAL';
      case 'convexity':
        return 'Convexity';
      case 'pd':
        return 'PD';
      case 'lgd':
        return 'LGD';
      default:
        return metric;
    }
  };

  // Add a mapping for tooltips/hints for key metrics
  const metricHints: Record<string, string> = {
    price: 'Clean price as a percentage of par value',
    fair_value: 'Estimated fair value of the loan',
    yield: 'Yield to maturity or exit',
    duration: 'Modified duration (years)',
    wal: 'Weighted average life (years)',
    convexity: 'Convexity (sensitivity to interest rate changes)',
    pd: 'Probability of Default',
    lgd: 'Loss Given Default',
    ead: 'Exposure at Default',
    credit_spread: 'Credit spread over benchmark',
    market_yield: 'Market yield for the loan',
    fair_value_ranges: 'Range of possible fair values and prices',
    prior_scenario: 'Metrics from the prior scenario for comparison',
    sector_allocation_summary: 'Summary of sector allocation for this loan',
  };

  return (
    <div className="page-container" data-test-id="pricing-comparison-page">
      <div className="page-inner" data-test-id="pricing-comparison-inner">
        {/* Header */}
        <div className="mb-8" data-test-id="pricing-comparison-header">
          <div
            className="flex justify-between items-center"
            data-test-id="header-content"
          >
            <div data-test-id="header-title-section">
              <h1 className="section-title mb-2" data-test-id="page-title">
                Pricing Scenario Comparison
              </h1>
              <p className="section-subtitle" data-test-id="page-subtitle">
                {loanComparisons.length > 0
                  ? `Comparing ${loanComparisons.length} loans across different scenarios`
                  : 'Select loans to compare pricing scenarios'}
              </p>
            </div>
            <div className="flex gap-3" data-test-id="header-actions">
              <Button
                variant="outline"
                onClick={() => setShowLoanSelector(true)}
                disabled={selectedLoans.length >= 4}
                className="flex items-center gap-2"
                data-test-id="add-loan-button"
              >
                <Plus className="h-4 w-4" data-test-id="add-loan-icon" />
                Add Loan
              </Button>
              <Button
                variant="outline"
                onClick={handleExport}
                disabled={exporting || loanComparisons.length === 0}
                className="flex items-center gap-2"
                data-test-id="export-comparison-button"
              >
                <Download
                  className={`h-4 w-4 ${exporting ? 'animate-spin' : ''}`}
                  data-test-id="export-comparison-icon"
                />
                {exporting ? 'Exporting...' : 'Export Comparison'}
              </Button>
            </div>
          </div>
        </div>

        {/* Loan Selector Modal */}
        {showLoanSelector && (
          <div
            className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50"
            data-test-id="loan-selector-modal"
          >
            <div
              className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white"
              data-test-id="loan-selector-modal-content"
            >
              <div className="mt-3" data-test-id="loan-selector-modal-inner">
                <div
                  className="flex justify-between items-center mb-4"
                  data-test-id="loan-selector-modal-header"
                >
                  <h3
                    className="text-lg font-medium text-gray-900"
                    data-test-id="loan-selector-modal-title"
                  >
                    Select Loan to Compare
                  </h3>
                  <button
                    onClick={() => setShowLoanSelector(false)}
                    className="text-gray-400 hover:text-gray-600"
                    data-test-id="loan-selector-modal-close"
                  >
                    <X
                      className="w-6 h-6"
                      data-test-id="loan-selector-modal-close-icon"
                    />
                  </button>
                </div>
                <div
                  className="max-h-96 overflow-y-auto"
                  data-test-id="loan-selector-list"
                >
                  {availableLoans
                    .filter((loan) => !selectedLoans.includes(loan.id))
                    .map((loan, index) => (
                      <div
                        key={loan.id}
                        className="p-4 border-b hover:bg-gray-50 cursor-pointer"
                        onClick={() => handleAddLoan(loan.id)}
                        data-test-id={`loan-selector-item-${index}`}
                      >
                        <div
                          className="flex justify-between items-center"
                          data-test-id={`loan-selector-item-content-${index}`}
                        >
                          <div
                            data-test-id={`loan-selector-item-info-${index}`}
                          >
                            <p
                              className="font-medium"
                              data-test-id={`loan-selector-item-name-${index}`}
                            >
                              {loan.name}
                            </p>
                            <p
                              className="text-sm text-gray-600"
                              data-test-id={`loan-selector-item-details-${index}`}
                            >
                              {loan.sector} • {formatCurrency(loan.balance)}
                            </p>
                          </div>
                          <Plus
                            className="h-5 w-5 text-gray-400"
                            data-test-id={`loan-selector-item-add-icon-${index}`}
                          />
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Selected Loans */}
        {selectedLoans.length > 0 && (
          <div className="mb-6" data-test-id="selected-loans-section">
            <div
              className="flex flex-wrap gap-2"
              data-test-id="selected-loans-list"
            >
              {selectedLoans.map((loanId, index) => {
                const loan = availableLoans.find((l) => l.id === loanId);
                return loan ? (
                  <div
                    key={loanId}
                    className="inline-flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-700 rounded-full"
                    data-test-id={`selected-loan-${index}`}
                  >
                    <span
                      className="text-sm font-medium"
                      data-test-id={`selected-loan-name-${index}`}
                    >
                      {loan.name}
                    </span>
                    <button
                      onClick={() => handleRemoveLoan(loanId)}
                      className="hover:text-blue-900"
                      data-test-id={`selected-loan-remove-${index}`}
                    >
                      <X
                        className="h-4 w-4"
                        data-test-id={`selected-loan-remove-icon-${index}`}
                      />
                    </button>
                  </div>
                ) : null;
              })}
            </div>
          </div>
        )}

        {loading ? (
          <div
            className="flex items-center justify-center h-64"
            data-test-id="loading-section"
          >
            <Loader2
              className="h-8 w-8 animate-spin text-gray-400"
              data-test-id="loading-spinner"
            />
            <span className="ml-2 text-gray-600" data-test-id="loading-text">
              Loading comparison data...
            </span>
          </div>
        ) : loanComparisons.length > 0 ? (
          <>
            {/* Real Data Comparison Table */}
            <Card
              className="card table-responsive mb-8"
              data-test-id="loan-comparison-table-card"
            >
              <h2
                className="card-title mb-4"
                data-test-id="loan-comparison-table-title"
              >
                Loan Comparison
              </h2>
              <table className="table" data-test-id="loan-comparison-table">
                <thead
                  className="table-header"
                  data-test-id="loan-comparison-table-header"
                >
                  <tr data-test-id="loan-comparison-header-row-1">
                    <th
                      className="table-cell-header"
                      data-test-id="metric-header"
                    >
                      Metric
                    </th>
                    {loanComparisons.map((loan, index) => (
                      <th
                        key={loan.loan_id}
                        className="table-cell-header"
                        colSpan={3}
                        data-test-id={`loan-header-${index}`}
                      >
                        {loan.loan_name}
                      </th>
                    ))}
                  </tr>
                  <tr data-test-id="loan-comparison-header-row-2">
                    <th
                      className="table-cell-header"
                      data-test-id="empty-header"
                    ></th>
                    {loanComparisons.map((loan, index) => (
                      <React.Fragment key={loan.loan_id}>
                        <th
                          className="table-cell-header text-xs"
                          data-test-id={`base-scenario-header-${index}`}
                        >
                          Base
                        </th>
                        <th
                          className="table-cell-header text-xs"
                          data-test-id={`stress-scenario-header-${index}`}
                        >
                          Stress
                        </th>
                        <th
                          className="table-cell-header text-xs"
                          data-test-id={`default-scenario-header-${index}`}
                        >
                          Default
                        </th>
                      </React.Fragment>
                    ))}
                  </tr>
                </thead>
                <tbody
                  className="bg-white divide-y divide-gray-200"
                  data-test-id="loan-comparison-table-body"
                >
                  {selectedMetrics.map((metric, metricIndex) => (
                    <tr
                      key={metric}
                      className="hover:bg-gray-50"
                      data-test-id={`metric-row-${metricIndex}`}
                    >
                      <td
                        className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                        data-test-id={`metric-label-${metricIndex}`}
                      >
                        {getMetricLabel(metric)}
                      </td>
                      {loanComparisons.map((loan, loanIndex) => (
                        <React.Fragment key={loan.loan_id}>
                          <td
                            className="px-6 py-4 whitespace-nowrap text-sm text-gray-900"
                            data-test-id={`metric-value-base-${metricIndex}-${loanIndex}`}
                          >
                            {getMetricValueFromValuation(
                              loan.scenarios.base_case,
                              metric
                            )}
                          </td>
                          <td
                            className="px-6 py-4 whitespace-nowrap text-sm text-gray-900"
                            data-test-id={`metric-value-stress-${metricIndex}-${loanIndex}`}
                          >
                            {getMetricValueFromValuation(
                              loan.scenarios.stress_case,
                              metric
                            )}
                          </td>
                          <td
                            className="px-6 py-4 whitespace-nowrap text-sm text-gray-900"
                            data-test-id={`metric-value-default-${metricIndex}-${loanIndex}`}
                          >
                            {getMetricValueFromValuation(
                              loan.scenarios.custom_scenario,
                              metric
                            )}
                          </td>
                        </React.Fragment>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>

            {/* Loan Detail Cards */}
            <div
              className="grid grid-cols-1 md:grid-cols-2 gap-6"
              data-test-id="loan-detail-cards"
            >
              {loanComparisons.map((loan, index) => (
                <Card
                  key={loan.loan_id}
                  className="p-6"
                  data-test-id={`loan-detail-card-${index}`}
                >
                  <h3
                    className="text-lg font-semibold text-gray-900 mb-4"
                    data-test-id={`loan-detail-card-title-${index}`}
                  >
                    {loan.loan_name}
                  </h3>
                  <div
                    className="grid grid-cols-3 gap-4"
                    data-test-id={`loan-scenarios-grid-${index}`}
                  >
                    {loan.scenarios.base_case && (
                      <div
                        className="bg-blue-50 p-4 rounded-lg"
                        data-test-id={`base-case-card-${index}`}
                      >
                        <h4
                          className="font-medium text-sm text-blue-900 mb-2"
                          data-test-id={`base-case-title-${index}`}
                        >
                          Base Case
                        </h4>
                        <div
                          className="space-y-1"
                          data-test-id={`base-case-metrics-${index}`}
                        >
                          <p
                            className="text-xs text-gray-600"
                            data-test-id={`base-case-price-${index}`}
                          >
                            Price:{' '}
                            <span className="font-medium">
                              {formatPercentage(
                                loan.scenarios.base_case?.price_clean_decimal
                              )}
                            </span>
                          </p>
                          <p
                            className="text-xs text-gray-600"
                            data-test-id={`base-case-yield-${index}`}
                          >
                            Yield:{' '}
                            <span className="font-medium">
                              {formatPercentage(
                                loan.scenarios.base_case?.component_yield_pct
                              )}
                            </span>
                          </p>
                        </div>
                      </div>
                    )}
                    {loan.scenarios.stress_case && (
                      <div
                        className="bg-yellow-50 p-4 rounded-lg"
                        data-test-id={`stress-case-card-${index}`}
                      >
                        <h4
                          className="font-medium text-sm text-yellow-900 mb-2"
                          data-test-id={`stress-case-title-${index}`}
                        >
                          Stress Case
                        </h4>
                        <div
                          className="space-y-1"
                          data-test-id={`stress-case-metrics-${index}`}
                        >
                          <p
                            className="text-xs text-gray-600"
                            data-test-id={`stress-case-price-${index}`}
                          >
                            Price:{' '}
                            <span className="font-medium">
                              {formatPercentage(
                                loan.scenarios.stress_case?.price_clean_decimal
                              )}
                            </span>
                          </p>
                          <p
                            className="text-xs text-gray-600"
                            data-test-id={`stress-case-yield-${index}`}
                          >
                            Yield:{' '}
                            <span className="font-medium">
                              {formatPercentage(
                                loan.scenarios.stress_case?.component_yield_pct
                              )}
                            </span>
                          </p>
                        </div>
                      </div>
                    )}
                    {loan.scenarios.custom_scenario && (
                      <div
                        className="bg-red-50 p-4 rounded-lg"
                        data-test-id={`default-case-card-${index}`}
                      >
                        <h4
                          className="font-medium text-sm text-red-900 mb-2"
                          data-test-id={`default-case-title-${index}`}
                        >
                          Default Case
                        </h4>
                        <div
                          className="space-y-1"
                          data-test-id={`default-case-metrics-${index}`}
                        >
                          <p
                            className="text-xs text-gray-600"
                            data-test-id={`default-case-price-${index}`}
                          >
                            Price:{' '}
                            <span className="font-medium">
                              {formatPercentage(
                                loan.scenarios.custom_scenario
                                  ?.price_clean_decimal
                              )}
                            </span>
                          </p>
                          <p
                            className="text-xs text-gray-600"
                            data-test-id={`default-case-yield-${index}`}
                          >
                            Yield:{' '}
                            <span className="font-medium">
                              {formatPercentage(
                                loan.scenarios.custom_scenario
                                  ?.component_yield_pct
                              )}
                            </span>
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          </>
        ) : selectedLoans.length === 0 ? (
          <Card
            className="p-8 text-center"
            data-test-id="no-loans-selected-card"
          >
            <p
              className="text-gray-600 mb-4"
              data-test-id="no-loans-selected-text"
            >
              No loans selected for comparison
            </p>
            <Button
              onClick={() => setShowLoanSelector(true)}
              className="flex items-center gap-2 mx-auto"
              data-test-id="select-loans-button"
            >
              <Plus className="h-4 w-4" data-test-id="select-loans-icon" />
              Select Loans to Compare
            </Button>
          </Card>
        ) : null}

        {/* Keep the original mock data display below for reference */}
        <div
          className="mt-16 pt-8 border-t border-gray-200"
          data-test-id="sample-analysis-section"
        >
          <h2
            className="text-xl font-semibold text-gray-900 mb-6"
            data-test-id="sample-analysis-title"
          >
            Sample Scenario Analysis
          </h2>
        </div>

        {/* Loan Information */}
        <Card className="card mb-8" data-test-id="sample-loan-info-card">
          <h2 className="card-title mb-4" data-test-id="sample-loan-info-title">
            Loan Information
          </h2>
          <div className="grid-3" data-test-id="sample-loan-info-grid">
            <div data-test-id="sample-loan-name-section">
              <p
                className="text-sm text-gray-600"
                data-test-id="sample-loan-name-label"
              >
                Loan Name
              </p>
              <p
                className="font-medium text-gray-900"
                data-test-id="sample-loan-name-value"
              >
                {comparisonData[0]?.loan_identification.loan_name}
              </p>
            </div>
            <div data-test-id="sample-loan-sector-section">
              <p
                className="text-sm text-gray-600"
                data-test-id="sample-loan-sector-label"
              >
                Sector
              </p>
              <p
                className="font-medium text-gray-900"
                data-test-id="sample-loan-sector-value"
              >
                {comparisonData[0]?.property_details.sector}
              </p>
            </div>
            <div data-test-id="sample-loan-balance-section">
              <p
                className="text-sm text-gray-600"
                data-test-id="sample-loan-balance-label"
              >
                Current Balance
              </p>
              <p
                className="font-medium text-gray-900"
                data-test-id="sample-loan-balance-value"
              >
                {formatCurrency(
                  comparisonData[0]?.loan_amounts.current_balance || 0,
                  (comparisonData[0]?.loan_amounts.currency || 'USD') as 'USD' | 'EUR' | 'GBP' | 'CAD' | 'AUD'
                )}
              </p>
            </div>
          </div>
        </Card>

        {/* Comparison Table */}
        <Card
          className="card table-responsive"
          data-test-id="sample-comparison-table-card"
        >
          <table className="table" data-test-id="sample-comparison-table">
            <thead
              className="table-header"
              data-test-id="sample-comparison-table-header"
            >
              <tr data-test-id="sample-comparison-header-row">
                <th
                  className="table-cell-header"
                  data-test-id="sample-metric-header"
                >
                  Metric
                </th>
                {comparisonData.map((scenario, index) => (
                  <th
                    key={scenario.pricing_scenario}
                    className="table-cell-header"
                    data-test-id={`sample-scenario-header-${index}`}
                  >
                    {scenario.pricing_scenario}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody
              className="bg-white divide-y divide-gray-200"
              data-test-id="sample-comparison-table-body"
            >
              {selectedMetrics.map((metric, metricIndex) => (
                <tr
                  key={metric}
                  className="hover:bg-gray-50"
                  data-test-id={`sample-metric-row-${metricIndex}`}
                >
                  <td
                    className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                    data-test-id={`sample-metric-label-${metricIndex}`}
                  >
                    <span title={metricHints[metric] || getMetricLabel(metric)}>
                      {getMetricLabel(metric)}
                    </span>
                  </td>
                  {comparisonData.map((scenario, scenarioIndex) => (
                    <td
                      key={`${scenario.pricing_scenario}-${metric}`}
                      className="px-6 py-4 whitespace-nowrap text-sm text-gray-900"
                      data-test-id={`sample-metric-value-${metricIndex}-${scenarioIndex}`}
                    >
                      <span
                        title={
                          metricHints[metric] ||
                          getMetricValue(scenario, metric)
                        }
                      >
                        {getMetricValue(scenario, metric)}
                      </span>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </Card>

        {/* Scenario Cards */}
        <div
          className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8"
          data-test-id="scenario-cards-grid"
        >
          {comparisonData.map((scenario, index) => (
            <Card
              key={scenario.pricing_scenario}
              className={`p-6 ${getScenarioColor(scenario.pricing_scenario)}`}
              data-test-id={`scenario-card-${index}`}
            >
              <h3
                className="text-lg font-semibold text-gray-900 mb-4"
                data-test-id={`scenario-card-title-${index}`}
              >
                {scenario.pricing_scenario}
              </h3>

              <div
                className="space-y-3"
                data-test-id={`scenario-card-metrics-${index}`}
              >
                <div
                  className="flex justify-between"
                  data-test-id={`scenario-card-credit-spread-${index}`}
                >
                  <span
                    className="text-sm text-gray-600"
                    data-test-id={`scenario-card-credit-spread-label-${index}`}
                  >
                    Credit Spread:
                  </span>
                  <span
                    className="font-medium"
                    data-test-id={`scenario-card-credit-spread-value-${index}`}
                  >
                    {formatPercentage(scenario.credit_spread)}
                  </span>
                </div>
                <div
                  className="flex justify-between"
                  data-test-id={`scenario-card-market-yield-${index}`}
                >
                  <span
                    className="text-sm text-gray-600"
                    data-test-id={`scenario-card-market-yield-label-${index}`}
                  >
                    Market Yield:
                  </span>
                  <span
                    className="font-medium"
                    data-test-id={`scenario-card-market-yield-value-${index}`}
                  >
                    {formatPercentage(scenario.market_yield)}
                  </span>
                </div>
                <div
                  className="flex justify-between"
                  data-test-id={`scenario-card-price-${index}`}
                >
                  <span
                    className="text-sm text-gray-600"
                    data-test-id={`scenario-card-price-label-${index}`}
                  >
                    Price:
                  </span>
                  <span
                    className="font-medium"
                    data-test-id={`scenario-card-price-value-${index}`}
                  >
                    {formatPercentage(scenario.valuation_results.price)}
                  </span>
                </div>
                <div
                  className="flex justify-between"
                  data-test-id={`scenario-card-fair-value-${index}`}
                >
                  <span
                    className="text-sm text-gray-600"
                    data-test-id={`scenario-card-fair-value-label-${index}`}
                  >
                    Fair Value:
                  </span>
                  <span
                    className="font-medium"
                    data-test-id={`scenario-card-fair-value-value-${index}`}
                  >
                    {formatCurrency(scenario.valuation_results.fair_value)}
                  </span>
                </div>
                <div
                  className="flex justify-between"
                  data-test-id={`scenario-card-pd-${index}`}
                >
                  <span
                    className="text-sm text-gray-600"
                    data-test-id={`scenario-card-pd-label-${index}`}
                  >
                    PD:
                  </span>
                  <span
                    className="font-medium"
                    data-test-id={`scenario-card-pd-value-${index}`}
                  >
                    {formatPercentage(scenario.risk_metrics.pd)}
                  </span>
                </div>
                <div
                  className="flex justify-between"
                  data-test-id={`scenario-card-lgd-${index}`}
                >
                  <span
                    className="text-sm text-gray-600"
                    data-test-id={`scenario-card-lgd-label-${index}`}
                  >
                    LGD:
                  </span>
                  <span
                    className="font-medium"
                    data-test-id={`scenario-card-lgd-value-${index}`}
                  >
                    {formatPercentage(scenario.risk_metrics.lgd)}
                  </span>
                </div>
              </div>

              {scenario.price_changes.delta_price !== 0 && (
                <div
                  className="mt-4 p-3 bg-white rounded-lg"
                  data-test-id={`scenario-card-price-change-${index}`}
                >
                  <p
                    className="text-sm font-medium text-gray-900 mb-2"
                    data-test-id={`scenario-card-price-change-label-${index}`}
                  >
                    Price Change from Base
                  </p>
                  <p
                    className={`text-lg font-bold ${scenario.price_changes.delta_price > 0 ? 'text-green-600' : 'text-red-600'}`}
                    data-test-id={`scenario-card-price-change-value-${index}`}
                  >
                    {scenario.price_changes.delta_price > 0 ? '+' : ''}
                    {formatPercentage(scenario.price_changes.delta_price)}
                  </p>
                </div>
              )}

              {/* Advanced Section */}
              <div
                className="mt-6 pt-6 border-t border-gray-200"
                data-test-id={`scenario-card-advanced-${index}`}
              >
                <h4
                  className="text-md font-semibold text-gray-900 mb-3"
                  data-test-id={`scenario-card-advanced-title-${index}`}
                >
                  Advanced Metrics
                </h4>
                <div
                  className="grid grid-cols-1 md:grid-cols-2 gap-3"
                  data-test-id={`scenario-card-advanced-grid-${index}`}
                >
                  <div
                    data-test-id={`scenario-card-fair-value-ranges-${index}`}
                  >
                    <p
                      className="text-sm text-gray-600"
                      data-test-id={`scenario-card-fair-value-ranges-label-${index}`}
                    >
                      Fair Value Ranges:
                    </p>
                    <p
                      className="font-medium text-gray-900"
                      data-test-id={`scenario-card-fair-value-ranges-value-${index}`}
                    >
                      Lower Price:{' '}
                      {formatPercentage(scenario.fair_value_ranges.lower_price)}
                      <br />
                      Upper Price:{' '}
                      {formatPercentage(scenario.fair_value_ranges.upper_price)}
                      <br />
                      Lower Fair Value:{' '}
                      {formatCurrency(scenario.fair_value_ranges.lower_fv)}
                      <br />
                      Upper Fair Value:{' '}
                      {formatCurrency(scenario.fair_value_ranges.upper_fv)}
                      <br />
                      Lower Market Yield:{' '}
                      {formatPercentage(scenario.fair_value_ranges.lower_mey)}
                      <br />
                      Upper Market Yield:{' '}
                      {formatPercentage(scenario.fair_value_ranges.upper_mey)}
                    </p>
                  </div>
                  <div data-test-id={`scenario-card-prior-scenario-${index}`}>
                    <p
                      className="text-sm text-gray-600"
                      data-test-id={`scenario-card-prior-scenario-label-${index}`}
                    >
                      Prior Scenario:
                    </p>
                    <p
                      className="font-medium text-gray-900"
                      data-test-id={`scenario-card-prior-scenario-value-${index}`}
                    >
                      Price:{' '}
                      {formatPercentage(scenario.prior_scenario.price_prior)}
                      <br />
                      Credit Spread:{' '}
                      {formatPercentage(
                        scenario.prior_scenario.credit_spread_prior
                      )}
                      <br />
                      Market Yield:{' '}
                      {formatPercentage(
                        scenario.prior_scenario.market_yield_prior
                      )}
                      <br />
                      WAL: {formatPercentage(scenario.prior_scenario.wal_prior)}
                      <br />
                      Duration:{' '}
                      {scenario.prior_scenario.duration_prior.toFixed(1)}y
                      <br />
                      Loan Status: {scenario.prior_scenario.loan_status_prior}
                    </p>
                  </div>
                  <div
                    data-test-id={`scenario-card-sector-allocation-${index}`}
                  >
                    <p
                      className="text-sm text-gray-600"
                      data-test-id={`scenario-card-sector-allocation-label-${index}`}
                    >
                      Sector Allocation:
                    </p>
                    <p
                      className="font-medium text-gray-900"
                      data-test-id={`scenario-card-sector-allocation-value-${index}`}
                    >
                      {scenario.sector_allocation_summary}
                    </p>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Risk Analysis */}
        <Card className="p-6 mt-8" data-test-id="risk-analysis-card">
          <h3
            className="text-lg font-semibold text-gray-900 mb-4"
            data-test-id="risk-analysis-title"
          >
            Risk Analysis
          </h3>
          <div
            className="grid grid-cols-1 md:grid-cols-2 gap-6"
            data-test-id="risk-analysis-grid"
          >
            <div data-test-id="pd-analysis-section">
              <h4
                className="font-medium text-gray-900 mb-3"
                data-test-id="pd-analysis-title"
              >
                Probability of Default (PD)
              </h4>
              <div className="space-y-2" data-test-id="pd-analysis-list">
                {comparisonData.map((scenario, index) => (
                  <div
                    key={scenario.pricing_scenario}
                    className="flex justify-between items-center"
                    data-test-id={`pd-analysis-item-${index}`}
                  >
                    <span
                      className="text-sm text-gray-600"
                      data-test-id={`pd-analysis-scenario-${index}`}
                    >
                      {scenario.pricing_scenario}
                    </span>
                    <div
                      className="flex items-center space-x-2"
                      data-test-id={`pd-analysis-value-container-${index}`}
                    >
                      <div
                        className="w-24 bg-gray-200 rounded-full h-2"
                        data-test-id={`pd-analysis-bar-bg-${index}`}
                      >
                        <div
                          className="bg-red-600 h-2 rounded-full"
                          style={{
                            width: `${Math.min(scenario.risk_metrics.pd, 100)}%`,
                          }}
                          data-test-id={`pd-analysis-bar-fill-${index}`}
                        ></div>
                      </div>
                      <span
                        className="text-sm font-medium"
                        data-test-id={`pd-analysis-percentage-${index}`}
                      >
                        {formatPercentage(scenario.risk_metrics.pd)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div data-test-id="lgd-analysis-section">
              <h4
                className="font-medium text-gray-900 mb-3"
                data-test-id="lgd-analysis-title"
              >
                Loss Given Default (LGD)
              </h4>
              <div className="space-y-2" data-test-id="lgd-analysis-list">
                {comparisonData.map((scenario, index) => (
                  <div
                    key={scenario.pricing_scenario}
                    className="flex justify-between items-center"
                    data-test-id={`lgd-analysis-item-${index}`}
                  >
                    <span
                      className="text-sm text-gray-600"
                      data-test-id={`lgd-analysis-scenario-${index}`}
                    >
                      {scenario.pricing_scenario}
                    </span>
                    <div
                      className="flex items-center space-x-2"
                      data-test-id={`lgd-analysis-value-container-${index}`}
                    >
                      <div
                        className="w-24 bg-gray-200 rounded-full h-2"
                        data-test-id={`lgd-analysis-bar-bg-${index}`}
                      >
                        <div
                          className="bg-orange-600 h-2 rounded-full"
                          style={{ width: `${scenario.risk_metrics.lgd}%` }}
                          data-test-id={`lgd-analysis-bar-fill-${index}`}
                        ></div>
                      </div>
                      <span
                        className="text-sm font-medium"
                        data-test-id={`lgd-analysis-percentage-${index}`}
                      >
                        {formatPercentage(scenario.risk_metrics.lgd)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default PricingComparisonPage;
